import { Observer, Store, UserRole } from '@nextforce/api'
import { Icon, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { filterStores } from 'management_team/util'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ListEmptyComponent } from 'shared/components/empty_list'
import { LastUpdatedPill } from 'shared/components/last_updated_pill'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import RefreshControl from 'shared/components/refresh_control'
import { MemberInfoView } from './member_info'
import { StoreSearchBar } from './store_search_bar'

interface Props {
  user: Observer | undefined
  role: UserRole | undefined
  isCurrentUser: boolean
  onPressCall: () => void
  onPressChat: () => void
  onPressEdit: () => void
  onPressRemove: () => void
  onPressChooseStores: () => void
  onRefresh: () => Promise<void>
  lastUpdatedAt?: number
  isProcessing?: boolean
  isRefreshing?: boolean
}

export function TeamMemberView({
  user,
  role,
  isCurrentUser,
  lastUpdatedAt,
  isProcessing,
  isRefreshing,
  onPressCall,
  onRefresh,
  onPressChat,
  onPressEdit,
  onPressRemove,
  onPressChooseStores,
}: Props) {
  const styles = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchEnabled, setSearchEnabled] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const shouldHaveStores = role === 'observer'
  const filteredStores = useMemo(() => {
    if (searchEnabled) {
      return filterStores(searchTerm, user?.stores)
    }
    return user?.stores
  }, [user?.stores, searchEnabled, searchTerm])

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <FlatList
          keyboardShouldPersistTaps={'handled'}
          data={filteredStores}
          renderItem={({ item }) => <StoreItem store={item} />}
          style={{ flex: 1 }}
          refreshControl={
            searchEnabled ? undefined : (
              <RefreshControl
                refreshing={refreshing}
                enabled={true}
                onRefresh={async () => {
                  setRefreshing(true)
                  await onRefresh()
                  setRefreshing(false)
                }}
              />
            )
          }
          ListEmptyComponent={
            shouldHaveStores ? (
              <ListEmptyComponent title={t('noStoresFound')} />
            ) : null
          }
          ListHeaderComponent={
            searchEnabled ? (
              <StoreSearchBar
                filteredStores={filteredStores}
                setSearchEnabled={setSearchEnabled}
                setSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
              />
            ) : (
              <View style={styles.memberInfoContainer}>
                <LastUpdatedPill
                  lastUpdatedAt={lastUpdatedAt}
                  isRefreshing={isRefreshing}
                  isProcessing={isProcessing}
                  onPress={onRefresh}
                />
                <MemberInfoView
                  user={user}
                  role={role}
                  isCurrentUser={isCurrentUser}
                  onPressCall={onPressCall}
                  onPressChat={onPressChat}
                  onPressEdit={onPressEdit}
                  onPressRemove={onPressRemove}
                />
                <StoreListHeaderComponent
                  user={user}
                  setSearchEnabled={setSearchEnabled}
                  onPressChooseStores={onPressChooseStores}
                />
              </View>
            )
          }
          stickyHeaderIndices={[0]}
        />
      </View>
    </SafeAreaView>
  )
}

function StoreListHeaderComponent({
  user,
  setSearchEnabled,
  onPressChooseStores,
}: {
  user: Observer | undefined
  setSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>
  onPressChooseStores: () => void
}) {
  const styles = useStyles()
  return (
    <View style={styles.listHeaderContainer}>
      {user?.stores?.length ? (
        <View style={styles.listHeader}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={[styles.text, styles.title]}>{t('stores')}</Text>
            <View style={styles.storeCountContainer}>
              <Text style={styles.storeCountText}>
                {user?.stores?.length ?? 0}
              </Text>
            </View>
          </View>
          <View style={styles.buttonRow}>
            <PressableOpacity onPress={onPressChooseStores}>
              <View style={styles.storeButtonContainer}>
                <View style={styles.storeButton}>
                  <Icon name="edit" type="feather" size={15} />
                </View>
                <Text style={styles.buttonLabel}>{t('addRemove')}</Text>
              </View>
            </PressableOpacity>
            <PressableOpacity onPress={() => setSearchEnabled(true)}>
              <View style={styles.storeButtonContainer}>
                <View style={styles.storeButton}>
                  <Icon name="search" type="ionicons" size={15} />
                </View>
                <Text style={styles.buttonLabel}>{t('search')}</Text>
              </View>
            </PressableOpacity>
          </View>
        </View>
      ) : null}
    </View>
  )
}

function StoreItem({ store }: { store: Store }) {
  const styles = useStyles()
  return (
    <View style={styles.card}>
      <View style={styles.storeItemContainer}>
        <View>
          <Text style={styles.text}>
            {store.storeNumber} {store.name}
          </Text>
        </View>
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  card: {
    backgroundColor: theme.colors.white,
    width: '100%',
    marginBottom: 2,
    padding: 10,
  },
  listHeaderContainer: {
    backgroundColor: theme.colors.white,
    width: '100%',
    marginBottom: 2,
    padding: 10,
    marginTop: 5,
  },
  text: {
    marginTop: 2,
    fontSize: 16,
  },

  title: { fontSize: 16 },
  storeItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  storeCountContainer: {
    margin: 5,
    padding: 5,
    backgroundColor: theme.colors.peach,
    borderRadius: 50,
  },
  storeCountText: {
    fontSize: 16,
    paddingHorizontal: 10,
    color: 'black',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  storeButtonContainer: {
    alignItems: 'center',
    marginHorizontal: 10,
  },
  storeButton: {
    padding: 0,
    borderWidth: 0.5,
    width: 25,
    height: 25,
    borderColor: theme.colors.grey3,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 15,
    backgroundColor: theme.colors.white,
  },
  buttonLabel: {
    fontSize: 12,
    marginTop: 5,
  },
  memberInfoContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))
